import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Breadcrumbs,
  Link,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  RocketLaunch as RocketLaunchIcon,
  Assignment as AssignmentIcon,
  CalendarMonth as CalendarMonthIcon,
  Email as EmailIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  Folder as FolderIcon,
  Create as CreateIcon,
  TextFields as TextFieldsIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Lightbulb as LightbulbIcon,
  Home as HomeIcon,
  Computer as ComputerIcon,
  EventNote as EventNoteIcon,
  AccountTree as AccountTreeIcon,
} from '@mui/icons-material';
import {
  categories,
  helpArticles,
  getArticlesByCategory,
  searchArticles,
  getCategoryInfo,
  HelpCategory,
  HelpArticle,
} from '../data/helpContent';

const categoryIcons: Record<string, React.ReactElement> = {
  RocketLaunch: <RocketLaunchIcon />,
  Assignment: <AssignmentIcon />,
  CalendarMonth: <CalendarMonthIcon />,
  Email: <EmailIcon />,
  People: <PeopleIcon />,
  Description: <DescriptionIcon />,
  Folder: <FolderIcon />,
  Create: <CreateIcon />,
  TextFields: <TextFieldsIcon />,
  Settings: <SettingsIcon />,
  AdminPanelSettings: <AdminPanelSettingsIcon />,
  Lightbulb: <LightbulbIcon />,
  Computer: <ComputerIcon />,
  EventNote: <EventNoteIcon />,
  AccountTree: <AccountTreeIcon />,
};

const Help: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<HelpCategory | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);

  // Filter articles based on search query
  const filteredArticles = useMemo(() => {
    if (searchQuery.trim() === '') {
      if (selectedCategory) {
        return getArticlesByCategory(selectedCategory);
      }
      return helpArticles;
    }
    return searchArticles(searchQuery);
  }, [searchQuery, selectedCategory]);

  // Group filtered articles by category for search results
  const articlesByCategory = useMemo(() => {
    const grouped: Record<HelpCategory, HelpArticle[]> = {} as Record<HelpCategory, HelpArticle[]>;
    filteredArticles.forEach(article => {
      if (!grouped[article.category]) {
        grouped[article.category] = [];
      }
      grouped[article.category].push(article);
    });
    return grouped;
  }, [filteredArticles]);

  const handleCategoryClick = (categoryId: HelpCategory) => {
    setSelectedCategory(categoryId);
    setSelectedArticle(null);
    setSearchQuery('');
  };

  const handleArticleClick = (article: HelpArticle) => {
    setSelectedArticle(article);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setSelectedArticle(null);
    setSearchQuery('');
  };

  const handleBackToArticleList = () => {
    setSelectedArticle(null);
  };

  // Render category overview
  const renderCategoryOverview = () => (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Skeena Hilfe & Dokumentation
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Willkommen im Hilfe-Center! Wählen Sie eine Kategorie oder nutzen Sie die Suche, um Antworten zu finden.
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {categories.map((category) => (
          <Grid item xs={12} sm={6} md={4} key={category.id}>
            <Card>
              <CardActionArea onClick={() => handleCategoryClick(category.id)}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Box sx={{ color: 'primary.main', fontSize: 40 }}>
                      {categoryIcons[category.icon]}
                    </Box>
                    <Typography variant="h6" component="h2">
                      {category.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {category.description}
                  </Typography>
                  <Chip
                    label={`${getArticlesByCategory(category.id).length} Artikel`}
                    size="small"
                    sx={{ mt: 2 }}
                  />
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  // Render article list for selected category or search results
  const renderArticleList = () => {
    const categoryInfo = selectedCategory ? getCategoryInfo(selectedCategory) : null;

    return (
      <Box>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleBackToCategories();
            }}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <HomeIcon fontSize="small" />
            Hilfe
          </Link>
          {categoryInfo && <Typography color="text.primary">{categoryInfo.name}</Typography>}
          {searchQuery && <Typography color="text.primary">Suchergebnisse</Typography>}
        </Breadcrumbs>

        {categoryInfo && (
          <>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Box sx={{ color: 'primary.main', fontSize: 48 }}>
                {categoryIcons[categoryInfo.icon]}
              </Box>
              <Box>
                <Typography variant="h4" component="h1">
                  {categoryInfo.name}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {categoryInfo.description}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
          </>
        )}

        {searchQuery && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {filteredArticles.length} Ergebnis{filteredArticles.length !== 1 ? 'se' : ''} für "{searchQuery}"
          </Alert>
        )}

        {searchQuery ? (
          // Search results grouped by category
          Object.entries(articlesByCategory).map(([categoryId, articles]) => {
            const catInfo = getCategoryInfo(categoryId as HelpCategory);
            return (
              <Box key={categoryId} sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {catInfo && categoryIcons[catInfo.icon]}
                  {catInfo?.name}
                </Typography>
                <List>
                  {articles.map((article) => (
                    <React.Fragment key={article.id}>
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => handleArticleClick(article)}
                          sx={{ borderRadius: 1 }}
                        >
                          <ListItemText
                            primary={article.title}
                            secondary={article.description}
                          />
                        </ListItemButton>
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            );
          })
        ) : (
          // Regular article list for selected category
          <List>
            {filteredArticles.map((article) => (
              <React.Fragment key={article.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleArticleClick(article)}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemText
                      primary={article.title}
                      secondary={article.description}
                    />
                  </ListItemButton>
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        )}

        {filteredArticles.length === 0 && (
          <Alert severity="warning">
            Keine Artikel gefunden. Versuchen Sie einen anderen Suchbegriff.
          </Alert>
        )}
      </Box>
    );
  };

  // Render article detail
  const renderArticleDetail = () => {
    if (!selectedArticle) return null;

    const categoryInfo = getCategoryInfo(selectedArticle.category);

    return (
      <Box>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleBackToCategories();
            }}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <HomeIcon fontSize="small" />
            Hilfe
          </Link>
          {categoryInfo && (
            <Link
              color="inherit"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleBackToArticleList();
              }}
            >
              {categoryInfo.name}
            </Link>
          )}
          <Typography color="text.primary">{selectedArticle.title}</Typography>
        </Breadcrumbs>

        <Paper sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            {categoryInfo && (
              <Chip
                icon={categoryIcons[categoryInfo.icon]}
                label={categoryInfo.name}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {selectedArticle.lastUpdated && (
              <Chip
                label={`Aktualisiert: ${selectedArticle.lastUpdated}`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>

          <Typography variant="h4" component="h1" gutterBottom>
            {selectedArticle.title}
          </Typography>

          <Typography variant="body1" color="text.secondary" paragraph>
            {selectedArticle.description}
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Box
            sx={{
              '& h3': { mt: 3, mb: 2, fontSize: '1.5rem', fontWeight: 500 },
              '& p': { mb: 2, lineHeight: 1.7 },
              '& ul, & ol': { mb: 2, pl: 3 },
              '& li': { mb: 1 },
              '& code': {
                bgcolor: 'grey.100',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              },
              '& strong': { fontWeight: 600 },
            }}
            dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
          />
        </Paper>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Durchsuchen Sie die Hilfe..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          variant="outlined"
        />
      </Paper>

      {/* Content */}
      {selectedArticle
        ? renderArticleDetail()
        : selectedCategory || searchQuery
        ? renderArticleList()
        : renderCategoryOverview()}
    </Box>
  );
};

export default Help;
